import { initWater, syncNavWordmark, type SceneHooks } from "../shared/water.js";
import { mountDepthSections } from "./depth.js";
import { mountLauncher } from "./games/launcher.js";

// ---------------------------------------------------------------------------
// User icon — particle/smoke cloud on hover
// ---------------------------------------------------------------------------

(() => {
  const btn = document.querySelector<HTMLAnchorElement>(".nav__user");
  const canvas = document.querySelector<HTMLCanvasElement>(".nav__user-canvas");
  if (!btn || !canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const CANVAS_DISPLAY = 80;
  const ICON_DISPLAY = 28;
  const SCALE = Math.min(window.devicePixelRatio || 1, 2);
  const SIZE = CANVAS_DISPLAY * SCALE;
  const ICON_SIZE = ICON_DISPLAY * SCALE;
  const ICON_OFFSET = (SIZE - ICON_SIZE) / 2;

  canvas.width = SIZE;
  canvas.height = SIZE;

  const iconColorRaw = getComputedStyle(document.documentElement)
    .getPropertyValue("--fg")
    .trim();
  const iconColor = /^#[0-9a-f]{6}$/i.test(iconColorRaw) ? iconColorRaw : "#ececf1";

  function drawIcon(c: CanvasRenderingContext2D, fill: string, alpha: number): void {
    c.save();
    c.globalAlpha = alpha;
    c.fillStyle = fill;
    const cx = SIZE / 2;
    const top = ICON_OFFSET;

    const headR = ICON_SIZE * 0.18;
    const headY = top + ICON_SIZE * 0.34;
    c.beginPath();
    c.arc(cx, headY, headR, 0, Math.PI * 2);
    c.fill();

    const bodyR = ICON_SIZE * 0.36;
    const bodyY = top + ICON_SIZE * 0.92;
    c.beginPath();
    c.arc(cx, bodyY, bodyR, Math.PI, Math.PI * 2);
    c.closePath();
    c.fill();
    c.restore();
  }

  // Sample anchor positions from the icon silhouette pixels.
  const off = document.createElement("canvas");
  off.width = SIZE;
  off.height = SIZE;
  const offCtx = off.getContext("2d");
  if (!offCtx) return;
  drawIcon(offCtx, "#ffffff", 1);
  const img = offCtx.getImageData(0, 0, SIZE, SIZE).data;

  interface Particle {
    bx: number;
    by: number;
    phase: number;
    phase2: number;
    speed: number;
    drift: number;
    rise: number;
    life: number;
    size: number;
  }
  const particles: Particle[] = [];
  for (let y = 0; y < SIZE; y += 1) {
    for (let x = 0; x < SIZE; x += 1) {
      const a = img[(y * SIZE + x) * 4 + 3]!;
      if (a > 128 && Math.random() < 0.55) {
        particles.push({
          bx: x,
          by: y,
          phase: Math.random() * Math.PI * 2,
          phase2: Math.random() * Math.PI * 2,
          speed: 0.6 + Math.random() * 1.4,
          drift: 0.5 + Math.random() * 1.5,
          rise: 0.4 + Math.random() * 1.2,
          life: Math.random(),
          size:
            Math.random() < 0.15
              ? 2 + Math.random() * 1.5
              : 0.8 + Math.random() * 1.2,
        });
      }
    }
  }

  function turb(x: number, y: number, t: number, p: Particle): number {
    const a =
      Math.sin(x * 0.06 + t * 1.1 + p.phase) +
      Math.cos(y * 0.05 - t * 1.3 + p.phase2);
    const b =
      Math.sin(x * 0.18 - t * 1.9 + p.phase2) * 0.6 +
      Math.cos(y * 0.21 + t * 1.7 + p.phase) * 0.6;
    return a + b;
  }

  // drawImage with globalAlpha on a pre-rendered puff is far cheaper than
  // building radial gradients per frame, and avoids the cold-start hitch when
  // the additive composite pipeline first runs.
  const PUFF_SIZE = 64;
  const puff = document.createElement("canvas");
  puff.width = PUFF_SIZE;
  puff.height = PUFF_SIZE;
  const puffCtx = puff.getContext("2d");
  if (!puffCtx) return;
  {
    const cx = PUFF_SIZE / 2;
    const ir = parseInt(iconColor.slice(1, 3), 16);
    const ig = parseInt(iconColor.slice(3, 5), 16);
    const ib = parseInt(iconColor.slice(5, 7), 16);
    const g = puffCtx.createRadialGradient(cx, cx, 0, cx, cx, cx);
    g.addColorStop(0, `rgba(${ir},${ig},${ib},1)`);
    g.addColorStop(0.6, `rgba(${ir},${ig},${ib},0.35)`);
    g.addColorStop(1, `rgba(${ir},${ig},${ib},0)`);
    puffCtx.fillStyle = g;
    puffCtx.fillRect(0, 0, PUFF_SIZE, PUFF_SIZE);
  }

  // Warm up the additive blending pipeline so the first hover doesn't stall.
  ctx.globalCompositeOperation = "lighter";
  ctx.globalAlpha = 0;
  ctx.drawImage(puff, 0, 0, 1, 1);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  ctx.clearRect(0, 0, SIZE, SIZE);

  let hovered = false;
  let t = 0;
  let time = 0;
  let last = performance.now();

  btn.addEventListener("mouseenter", () => {
    hovered = true;
  });
  btn.addEventListener("mouseleave", () => {
    hovered = false;
  });
  btn.addEventListener("focus", () => {
    hovered = true;
  });
  btn.addEventListener("blur", () => {
    hovered = false;
  });

  // Clicking the link puts focus on it; bfcache then freezes the page with
  // focus still on the button, so on restore the smoke keeps playing.
  // pagehide reset before freeze; pageshow reset on restore; blur the
  // button in both cases so a leftover focus doesn't re-trigger the effect.
  const resetState = (): void => {
    hovered = false;
    t = 0;
    last = performance.now();
    if (document.activeElement === btn) btn.blur();
  };
  window.addEventListener("pagehide", resetState);
  window.addEventListener("pageshow", resetState);

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) {
    drawIcon(ctx, iconColor, 1);
    return;
  }

  const frame = (now: number): void => {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    time += dt;

    const target = hovered ? 1 : 0;
    t += (target - t) * 0.1;

    ctx.clearRect(0, 0, SIZE, SIZE);

    if (t < 0.995) {
      drawIcon(ctx, iconColor, Math.pow(1 - t, 1.4));
    }

    if (t > 0.005) {
      ctx.globalCompositeOperation = "lighter";
      const ampBase = Math.pow(t, 1.2) * SCALE * 6.0;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i]!;
        const nx = turb(p.bx, p.by, time * p.speed, p);
        const ny = turb(p.bx + 53.1, p.by + 17.7, time * p.speed * 0.9, p);
        const amp = ampBase * p.drift;
        const rise = -Math.pow(t, 1.4) * 4 * p.rise;
        const x = p.bx + nx * amp;
        const y = p.by + ny * amp + rise;
        const flicker = 0.55 + 0.45 * Math.sin(time * 2.1 * p.speed + p.life * 6.28);
        const dist = Math.hypot(x - p.bx, y - p.by);
        const falloff = Math.max(0, 1 - dist / (SCALE * 14));
        const a = t * flicker * (0.18 + 0.55 * falloff);
        if (a <= 0.01) continue;
        const r = p.size * SCALE;
        const w = r * 2;
        ctx.globalAlpha = a;
        ctx.drawImage(puff, x - r, y - r, w, w);
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = "source-over";
    }

    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
})();

// ---------------------------------------------------------------------------
// Hero CTA: reveal email on first hover/tap, then keep it visible
// ---------------------------------------------------------------------------

document.querySelectorAll<HTMLElement>(".hero-overlay__cta").forEach((cta) => {
  const trigger = cta.querySelector<HTMLElement>(".hero-overlay__cta-trigger");
  if (!trigger) return;
  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  const reveal = (): void => {
    cta.classList.add("is-revealed");
    if (hideTimer !== undefined) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => cta.classList.remove("is-revealed"), 8000);
  };
  trigger.addEventListener("mouseenter", reveal);
  trigger.addEventListener("touchstart", reveal, { passive: true });
  cta.addEventListener("focusin", reveal);
});

// ---------------------------------------------------------------------------
// Hero water scene
// ---------------------------------------------------------------------------

const sceneHooks: SceneHooks = { pause: () => {}, resume: () => {} };

const waterEl = document.querySelector<HTMLElement>(".water");
if (waterEl) Object.assign(sceneHooks, initWater(waterEl));

const heroMarkEl = document.querySelector<HTMLElement>(".hero-mark");
const navEl = document.getElementById("nav");
if (heroMarkEl && navEl) syncNavWordmark(heroMarkEl, navEl);

mountLauncher(sceneHooks);

// ---------------------------------------------------------------------------
// Scroll-down depth sections (services + fathom)
// ---------------------------------------------------------------------------

mountDepthSections();
